import React from "react";
import { Form, Table, Input, InputNumber, Select, Checkbox, Space, List, Typography, Button } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import Decimal from "decimal.js";

const { Option } = Select;
const { Text } = Typography;

/**
 * @description Sección de conceptos/productos para facturas
 * Incluye tabla de productos y totales
 */
const ConceptosProductosSection = ({ 
    disabled = false,
}) => {
    
    const form = Form.useFormInstance();
    
    /**
     * @description Calcula automáticamente los valores de un concepto
     * @param {number} index - Índice del concepto en el array
     * @param {object} form - Instancia del formulario
     */
    const calcularConcepto = (index, formInstance) => {
        const conceptos = formInstance.getFieldValue('Conceptos') || [];
        const concepto = conceptos[index];
        
        if (!concepto) return;

        const cantidad = new Decimal(concepto.Cantidad || 0);
        const valorUnitario = new Decimal(concepto.ValorUnitario || 0);
        
        // Calcular Importe
        const importe = cantidad.mul(valorUnitario).toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber();
        
        // Actualizar Importe
        conceptos[index].Importe = importe;
        
        // Calcular Base de impuestos
        const base = importe;
        if (!conceptos[index].Impuestos) {
            conceptos[index].Impuestos = { Traslados: [{}] };
        }
        if (!conceptos[index].Impuestos.Traslados) {
            conceptos[index].Impuestos.Traslados = [{}];
        }
        if (!conceptos[index].Impuestos.Traslados[0]) {
            conceptos[index].Impuestos.Traslados[0] = {};
        }
        
        conceptos[index].Impuestos.Traslados[0].Base = base;
        conceptos[index].Impuestos.Traslados[0].Impuesto = conceptos[index].NoImpuesto ? "001" : "002";
        conceptos[index].Impuestos.Traslados[0].TasaOCuota = "0.160000";
        conceptos[index].Impuestos.Traslados[0].TipoFactor = "Tasa";
        
        // Calcular impuestos solo si NoImpuesto no está marcado como true
        const impuestos = conceptos[index].NoImpuesto 
            ? 0 
            : new Decimal(base).mul(new Decimal(0.16)).toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber();
        
        conceptos[index].Impuestos.Traslados[0].Importe = impuestos;
        conceptos[index].ObjetoImp = conceptos[index].NoImpuesto ? "01" : "02";
        
        // Calcular Total
        const total = new Decimal(importe).add(new Decimal(impuestos)).toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber();
        conceptos[index].Total = total;
        
        // Actualizar el formulario
        formInstance.setFieldsValue({ Conceptos: conceptos });
        
        // Calcular totales generales
        calcularTotales(formInstance);
    };

    /**
     * @description Calcula los totales generales del formulario
     * @param {object} form - Instancia del formulario
     */
    const calcularTotales = (formInstance) => {
        const conceptos = formInstance.getFieldValue('Conceptos') || [];
        
        let subtotal = new Decimal(0);
        let totalImpuestos = new Decimal(0);
        
        conceptos.forEach(concepto => {
            subtotal = subtotal.add(new Decimal(concepto.Importe || 0));
            totalImpuestos = totalImpuestos.add(new Decimal(concepto.Impuestos?.Traslados?.[0]?.Importe || 0));
        });
        
        const total = subtotal.add(totalImpuestos);
        
        formInstance.setFieldsValue({
            SubTotal: subtotal.toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber(),
            Impuestos: {
                TotalImpuestosTrasladados: totalImpuestos.toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber()
            },
            Total: total.toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber()
        });
    };

    return (
        <Form.Item
            noStyle
            shouldUpdate={(prevValues, curValues) => true}
        >
            {({ getFieldValue }) => {
                
                // Construcción dinámica de columnas según props
                const buildColumns = (remove) => {
                    let pipeline = [
                        {
                            title: "",
                            key: "actions",
                            width: 30,
                            fixed: "left",
                            render: (item, { name }) => (
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => {
                                        remove(name);
                                        // Recalcular totales después de eliminar
                                        setTimeout(() => calcularTotales(form), 100);
                                    }}
                                    disabled={disabled}
                                />
                            ),
                        },
                        {
                            title: "ClaveProdServ",
                            dataIndex: "ClaveProdServ",
                            key: "ClaveProdServ",
                            width: 40,
                            render: (item, { name }) => (
                                <Form.Item 
                                    name={[name, "ClaveProdServ"]} 
                                    noStyle
                                    rules={[{
                                        required: true,
                                        message: "Ingresé el codigo de producto o servicio adecuado"
                                    }]}
                                >
                                    <Input bordered={false} />
                                </Form.Item>
                            ),
                        },
                        {
                            width: 60,
                            fixed: "left",
                            title: "No Identificacion (SKU)",
                            dataIndex: "NoIdentificacion",
                            key: "NoIdentificacion",
                            render: (item, { name }) => (
                                <Form.Item name={[name, "NoIdentificacion"]} noStyle>
                                    <Input disabled={disabled} bordered={false} />
                                </Form.Item>
                            )
                        },
                    ];

                    // Descripción con campos ocultos
                    pipeline.push({
                        title: "Descripcion",
                        dataIndex: "Descripcion",
                        key: "Descripcion",
                        width: 100,
                        render: (item, { name }) => (
                            <span className="text-center">
                                <Form.Item name={[name, "_id"]} noStyle>
                                    <input type="hidden" />
                                </Form.Item>
                                <Form.Item name={[name, "NoIdentificacion"]} noStyle>
                                    <input type="hidden" />
                                </Form.Item>
                                <Form.Item name={[name, "Descripcion"]} noStyle>
                                    <input type="hidden" />
                                </Form.Item>
                                <Form.Item name={[name, "Descuento"]} noStyle>
                                    <input type="hidden" />
                                </Form.Item>
                                <Form.Item name={[name, "ObjetoImp"]} noStyle>
                                    <input type="hidden" />
                                </Form.Item>
                                <Form.Item name={[name, "Impuestos", "Traslados", 0, "Base"]} noStyle>
                                    <input type="hidden" />
                                </Form.Item>
                                <Form.Item name={[name, "Impuestos", "Traslados", 0, "Impuesto"]} noStyle>
                                    <input type="hidden" />
                                </Form.Item>
                                <Form.Item name={[name, "Impuestos", "Traslados", 0, "TasaOCuota"]} noStyle>
                                    <input type="hidden" />
                                </Form.Item>
                                <Form.Item name={[name, "Impuestos", "Traslados", 0, "TipoFactor"]} noStyle>
                                    <input type="hidden" />
                                </Form.Item>
                                <Form.Item name={[name, "Descripcion"]} noStyle>
                                    <Input.TextArea autoSize disabled={disabled} bordered={false} />
                                </Form.Item>
                            </span>
                        ),
                    });

                    // Cantidad
                    pipeline.push({
                        title: "Cantidad",
                        dataIndex: "Cantidad",
                        key: "Cantidad",
                        width: 50,
                        render: (text, { name }) => (
                            <Form.Item name={[name, "Cantidad"]}>
                                <InputNumber
                                    controls={false}
                                    disabled={disabled}
                                    bordered={false}
                                    className="text-center"
                                    onChange={() => calcularConcepto(name, form)}
                                />
                            </Form.Item>
                        ),
                    });

                    // Unidad
                    pipeline.push({
                        title: "Unidad",
                        dataIndex: "Unidad",
                        key: "Unidad",
                        width: 50,
                        render: (text, { name }) => (
                            <Form.Item
                                name={[name, 'ClaveUnidad']}
                                rules={[{
                                    required: true,
                                    whitespace: true,
                                    message: "Falta la Unidad",
                                }]}
                            >
                                <Select disabled={disabled} bordered={false}>
                                    <Option value={'H87'}>Pieza</Option>
                                    <Option value={'EA'}>Elemento</Option>
                                    <Option value={'KGM'}>Kilogramo</Option>
                                    <Option value={'MTR'}>Metro</Option>
                                    <Option value={'KT'}>Kit</Option>
                                    <Option value={'SET'}>Conjunto</Option>
                                    <Option value={'LTR'}>Litro</Option>
                                    <Option value={'XBX'}>Caja</Option>
                                    <Option value={'xun'}>Unidad</Option>
                                    <Option value={'GRM'}>Gramo</Option>
                                </Select>
                            </Form.Item>
                        ),
                    });

                    // Valor Unitario
                    pipeline.push({
                        title: "Valor Unitario",
                        dataIndex: "ValorUnitario",
                        key: "ValorUnitario",
                        width: 40,
                        render: (text, { name }) => (
                            <Form.Item name={[name, "ValorUnitario"]} noStyle>
                                <InputNumber
                                    style={{ textAlign: "end", width: "100%" }}
                                    controls={false}
                                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    disabled={disabled}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                    bordered={false}
                                    onChange={() => calcularConcepto(name, form)}
                                />
                            </Form.Item>
                        ),
                    });

                    // Importe
                    pipeline.push({
                        title: "Importe",
                        dataIndex: "Importe",
                        key: "Importe",
                        width: 50,
                        fixed: 'right',
                        render: (text, { name }) => (
                            <Form.Item name={[name, "Importe"]} noStyle>
                                <InputNumber
                                    style={{ textAlign: "end", width: "100%" }}
                                    controls={false}
                                    disabled={disabled}
                                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                    bordered={false}
                                    onChange={() => calcularTotales(form)}
                                />
                            </Form.Item>
                        )
                    });


                    // Impuestos
                    pipeline.push({
                        title: "Impuestos",
                        dataIndex: "Impuestos",
                        key: "Impuestos",
                        width: 50,
                        fixed: 'right',
                        render: (text, { name }) => (
                            <Space>
                                <Form.Item
                                    name={[name, "NoImpuesto"]}
                                    noStyle
                                    valuePropName="checked"
                                >
                                    <Checkbox onChange={() => calcularConcepto(name, form)} />
                                </Form.Item>
                                <Form.Item
                                    name={[name, "Impuestos", "Traslados", 0, "Importe"]}
                                    noStyle
                                >
                                    <InputNumber
                                        style={{ textAlign: "end", width: "100%" }}
                                        controls={false}
                                        disabled={disabled}
                                        formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                        bordered={false}
                                        onChange={() => calcularTotales(form)}
                                    />
                                </Form.Item>
                            </Space>
                        )
                    });

                    // Total
                    pipeline.push({
                        title: "Total",
                        dataIndex: "Total",
                        key: "Total",
                        width: 50,
                        fixed: 'right',
                        render: (text, { name }) => (
                            <>
                                <Form.Item name={[name, "Total"]} noStyle>
                                    <InputNumber
                                        style={{ textAlign: "end", width: "100%" }}
                                        controls={false}
                                        disabled={disabled}
                                        formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                        bordered={false}
                                        onChange={() => calcularTotales(form)}
                                    />
                                </Form.Item>
                                <Form.Item name={[name, "Importe"]} noStyle>
                                    <InputNumber
                                        style={{ textAlign: "end", width: "100%", display: 'none' }}
                                        controls={false}
                                        disabled={true}
                                        formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                        bordered={false}
                                    />
                                </Form.Item>
                            </>
                        )
                    });

                    return pipeline;
                };

                return (
                    <Form.List prefixCls="Conceptos" name="Conceptos">
                        {(fields, { remove }) => (
                            <Table
                                className="table-products"
                                size="small"
                                columns={buildColumns(remove)}
                                dataSource={fields}
                                pagination={false}
                                scroll={{ x: 1000 }}
                                bordered={true}
                            />
                        )}
                    </Form.List>
                );
            }}
        </Form.Item>
    );
};

export default ConceptosProductosSection;
